import React, { useState } from 'react'
import Layout from '../components/Layout'
import Table from '../components/common/Table'
import Button from '../components/common/Button'
import ContractInvoiceItem from '../components/ContractInvoiceItem'
import { useLoad, usePostRequest } from '../hooks/request'
import { BILLING_INVOICE_GENERATE, BILLING_INVOICE_LIST, BILLING_SUMMARY } from '../urls'
import { checkPermission as cp } from '../utils/auth'
import { PermissionDenied } from '../components/PermissionDenied'

function money(value) {
    return new Intl.NumberFormat('ru-RU').format(value || 0)
}

/** `YYYY-MM` ko'rinishidagi oxirgi 12 oy. */
function lastMonths(count = 12) {
    const now = new Date()
    return Array.from({ length: count }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    })
}

export default function ContractPayments() {
    const months = lastMonths()
    const [period, setPeriod] = useState(months[0])
    const [status, setStatus] = useState('')
    const [module, setModule] = useState('')
    const [search, setSearch] = useState('')
    const [notice, setNotice] = useState(null)

    const params = {
        period,
        ...(status ? { status } : {}),
        ...(module ? { module } : {}),
        ...(search ? { search } : {}),
    }

    const invoices = useLoad({ url: BILLING_INVOICE_LIST, params }, [period, status, module, search])
    const summary = useLoad({ url: BILLING_SUMMARY, params: { period, ...(module ? { module } : {}) } },
        [period, module])
    const generate = usePostRequest({ url: BILLING_INVOICE_GENERATE })

    async function onGenerate() {
        // eslint-disable-next-line no-restricted-globals, no-alert
        if (!confirm(`${period} — открыть месяц? Строки для всех активных компаний будут созданы.`)) return

        const { success, response } = await generate.request({ data: { period } })
        if (!success) {
            setNotice({ type: 'is-danger', text: 'Не удалось открыть месяц' })
            return
        }

        // Endpoint идемпотентный: повторный вызов ничего не создаёт.
        const created = response ? response.created : 0
        const existing = response ? response.existing : 0
        if (created === 0 && existing === 0) {
            setNotice({ type: 'is-warning', text: 'Подходящих компаний за этот месяц не найдено' })
        } else if (created === 0) {
            setNotice({ type: 'is-info', text: `Месяц уже открыт (${existing} компаний)` })
        } else {
            setNotice({
                type: 'is-success',
                text: `Добавлено ${created} компаний${existing > 0 ? `, ${existing} уже было` : ''}`,
            })
        }

        invoices.request()
        summary.request()
    }

    if (!cp('billing.view_contractinvoice')) {
        return <PermissionDenied />
    }

    const s = summary.response || {}

    return (
        <Layout>
            <span className="is-size-4">Платежи по договорам</span>

            {cp('billing.add_contractinvoice') ? (
                <Button
                    onClick={onGenerate}
                    loading={generate.loading}
                    text="Открыть месяц"
                    icon="ion-md-add"
                    className="is-pulled-right is-link is-outlined" />
            ) : null}

            <br /><br />

            {notice ? (
                <div className={`notification ${notice.type} is-light`}>
                    <button type="button" className="delete" onClick={() => setNotice(null)} />
                    {notice.text}
                </div>
            ) : null}

            <div className="columns">
                <div className="column">
                    <div className="box">
                        <p className="heading">Ожидается</p>
                        <p className="title is-5">{money(s.expectedTotal)}</p>
                    </div>
                </div>
                <div className="column">
                    <div className="box">
                        <p className="heading">Собрано</p>
                        <p className="title is-5 has-text-success">{money(s.paidTotal)}</p>
                    </div>
                </div>
                <div className="column">
                    <div className="box">
                        <p className="heading">Долг</p>
                        <p className="title is-5 has-text-danger">{money(s.debtTotal)}</p>
                    </div>
                </div>
                <div className="column">
                    <div className="box">
                        <p className="heading">Оплатили</p>
                        <p className="title is-5">{s.paidCount || 0} / {s.invoiceCount || 0}</p>
                    </div>
                </div>
            </div>

            <div className="columns">
                <div className="column is-3">
                    <div className="select is-fullwidth">
                        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
                            {months.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                </div>
                <div className="column is-2">
                    <div className="select is-fullwidth">
                        <select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="">Все статусы</option>
                            <option value="unpaid">Не оплачено</option>
                            <option value="partial">Частично</option>
                            <option value="paid">Оплачено</option>
                        </select>
                    </div>
                </div>
                <div className="column is-2">
                    <div className="select is-fullwidth">
                        <select value={module} onChange={(e) => setModule(e.target.value)}>
                            <option value="">Все модули</option>
                            <option value="zero">Nollik</option>
                            <option value="manthly">Oylik</option>
                        </select>
                    </div>
                </div>
                <div className="column">
                    <input
                        className="input"
                        placeholder="Поиск по названию или СТИР..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)} />
                </div>
            </div>

            <Table
                loading={invoices.loading}
                totalCount={invoices.response ? invoices.response.count : 0}
                items={invoices.response ? invoices.response.results : []}
                columns={{
                    company: 'Компания',
                    expected: 'Договор',
                    paid: 'Оплачено',
                    debt: 'Остаток',
                    status: 'Статус',
                    payments: 'Платежи',
                    actions: '',
                }}
                renderItem={(item) => (
                    <ContractInvoiceItem
                        key={item.id}
                        item={item}
                        onUpdate={() => {
                            invoices.request()
                            summary.request()
                        }} />
                )} />
        </Layout>
    )
}
